import React, { useRef, useEffect, useState, useMemo } from 'react';

// TypeScript interfaces for the component
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: {
    suggestions?: string[];
    type?: string;
    data?: any;
  };
}

interface ChatError {
  message: string;
}

interface ResponseDisplayProps {
  messages?: Message[];
  loading?: boolean;
  error?: ChatError | null;
  className?: string;
  style?: React.CSSProperties;
  onSelectSuggestion?: (suggestion: string) => void;
  enableFeedback?: boolean;
  onSendFeedback?: (messageId: string, feedback: 'positive' | 'negative') => void;
  enableRestaurantActions?: boolean;
  onViewDetails?: (restaurantData: any) => void;
  onBookReservation?: (restaurantData: any) => void;
  onViewMenu?: (restaurantData: any) => void;
  expandableCards?: boolean;
  enableSharing?: boolean;
  enableRatings?: boolean;
  onSubmitRating?: (rating: { restaurantId: string; rating: number; comment: string }) => void;
  onScrollButtonVisibilityChange?: (visible: boolean) => void;
}

/**
 * ResponseDisplay component that renders the chat messages
 * Converted to use Tailwind CSS classes for V2 specification compliance
 */
const ResponseDisplay: React.FC<ResponseDisplayProps> = ({ 
  messages = [], 
  loading = false,
  error = null,
  className,
  style,
  onSelectSuggestion,
  enableFeedback = false,
  onSendFeedback,
  enableRestaurantActions = false,
  onViewDetails,
  onBookReservation,
  onViewMenu,
  expandableCards = false,
  enableSharing = false,
  enableRatings = false,
  onSubmitRating,
  onScrollButtonVisibilityChange
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastAssistantMessageRef = useRef<HTMLDivElement>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [ratingDialog, setRatingDialog] = useState<{ restaurantId: string; restaurantName: string } | null>(null);
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [ratingComment, setRatingComment] = useState<string>('');
  
  // Memoize suggestion rendering to prevent unnecessary re-renders
  const renderSuggestions = useMemo(() => {
    return (suggestions: string[], messageId: string) => (
      <div className="flex flex-wrap gap-2 max-w-[80%] self-start mt-2">
        {suggestions.map((suggestion, index) => (
          <button
            key={`${messageId}-suggestion-${index}`}
            onClick={() => onSelectSuggestion?.(suggestion)}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
          >
            {suggestion}
          </button>
        ))}
      </div>
    );
  }, [onSelectSuggestion]);
  
  const toggleCardExpansion = (messageId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };
  

  // Function to scroll to bottom - optimized for stability
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight
      });
    }
  };
  
  // Auto-scroll to show new assistant messages when they appear - optimized for stability
  useEffect(() => {
    if (lastAssistantMessageRef.current && scrollContainerRef.current) {
      const messageElement = lastAssistantMessageRef.current;
      const container = scrollContainerRef.current;
      
      // Use setTimeout to avoid conflicts with DOM updates
      setTimeout(() => {
        // Check if the message is off-screen (below visible area)
        const messageTop = messageElement.offsetTop;
        const containerScrollTop = container.scrollTop;
        const containerHeight = container.clientHeight;
        const visibleBottom = containerScrollTop + containerHeight;
        
        if (messageTop >= visibleBottom) {
          // Scroll to show the top of the new assistant message - no smooth behavior
          container.scrollTo({
            top: messageTop - 20 // 20px padding from top
          });
        }
      }, 100); // Small delay to let DOM settle
    }
  }, [messages]);
  
  return (
    <div className={`flex-1 flex flex-col relative min-h-0 ${className || ''}`} style={style}>
      <div 
        ref={scrollContainerRef}
        data-testid="response-display"
        className="flex-1 overflow-y-auto p-4 bg-chat-bg flex flex-col gap-2"
        aria-live="polite"
        aria-label="Chat messages"
        tabIndex={0}
      >
      {messages.map((msg, index) => {
        // Check if this is a continuation of the previous assistant message
        const prevMessage = index > 0 ? messages[index - 1] : null;
        const isContinuation = prevMessage && 
          prevMessage.role === 'assistant' && 
          msg.role === 'assistant';
        
        // Check if this is the last assistant message
        const isLastAssistantMessage = msg.role === 'assistant' && 
          index === messages.length - 1;
        
        return (
          <div key={msg.id} className="flex flex-col gap-2">
            <div className={`flex gap-2 ${msg.role === 'user' ? 'self-end' : 'self-start'}`}>
              <div 
                ref={isLastAssistantMessage ? lastAssistantMessageRef : null}
                data-testid={msg.role === 'assistant' ? 'assistant-message' : 'message'}
                data-role={msg.role}
                data-timestamp={msg.timestamp}
                className={`
                  px-3 py-2 rounded-lg max-w-[80%]
                  ${msg.role === 'user' 
                    ? 'bg-user-message' 
                    : 'bg-bot-message'
                  }
                  ${isContinuation ? 'message-continuation' : ''}
                `}
              >
                {msg.content}
              </div>
            
            {/* Render feedback buttons for assistant messages */}
            {msg.role === 'assistant' && enableFeedback && (
              <div data-testid="feedback-buttons" className="flex gap-1 items-center">
                <button
                  data-testid="thumbs-up-button"
                  onClick={() => onSendFeedback?.(msg.id, 'positive')}
                  className="p-1 text-gray-400 hover:text-green-500 transition-colors"
                  aria-label="Good response"
                >
                  👍
                </button>
                <button
                  data-testid="thumbs-down-button"
                  onClick={() => onSendFeedback?.(msg.id, 'negative')}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  aria-label="Poor response"
                >
                  👎
                </button>
              </div>
            )}
          </div>
          
          {/* Render suggestion chips if present - optimized for stability */}
          {msg.metadata?.suggestions && msg.metadata.suggestions.length > 0 && 
            renderSuggestions(msg.metadata.suggestions, msg.id)
          }
          
          {/* Render restaurant card if present */}
          {msg.metadata?.type === 'restaurant' && msg.metadata.data && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 max-w-[80%] self-start shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-lg text-gray-900">
                  {msg.metadata.data.name}
                </h3>
                {expandableCards && (
                  <button
                    data-testid={expandedCards.has(msg.id) ? "collapse-card-button" : "expand-card-button"}
                    onClick={() => toggleCardExpansion(msg.id)}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    {expandedCards.has(msg.id) ? "▲ Less" : "▼ More"}
                  </button>
                )}
              </div>
              
              <p className="text-gray-600 mb-1">{msg.metadata.data.address}</p>
              
              {/* Show details based on expandable cards setting */}
              {(!expandableCards || expandedCards.has(msg.id)) && (
                <>
                  {msg.metadata.data.phone && (
                    <p className="text-gray-600 mb-1">{msg.metadata.data.phone}</p>
                  )}
                  {msg.metadata.data.hours && (
                    <p className="text-gray-600 mb-1">Hours: {msg.metadata.data.hours}</p>
                  )}
                  {msg.metadata.data.priceRange && (
                    <p className="text-gray-600 mb-3">Price: {'$'.repeat(parseInt(msg.metadata.data.priceRange))}</p>
                  )}
                </>
              )}
              
              {enableRestaurantActions && (
                <div className="flex gap-2 mt-3">
                  <button
                    data-testid="view-details-button"
                    onClick={() => msg.metadata?.data && onViewDetails?.(msg.metadata.data)}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                  >
                    View Details
                  </button>
                  <button
                    data-testid="book-reservation-button"
                    onClick={() => msg.metadata?.data && onBookReservation?.(msg.metadata.data)}
                    className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors"
                  >
                    Book Reservation
                  </button>
                  <button
                    data-testid="view-menu-button"
                    onClick={() => msg.metadata?.data && onViewMenu?.(msg.metadata.data)}
                    className="px-3 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600 transition-colors"
                  >
                    View Menu
                  </button>
                </div>
              )}
              
              {/* Share button */}
              {enableSharing && (
                <button
                  data-testid="share-button"
                  onClick={() => {
                    if (navigator.share && msg.metadata?.data) {
                      navigator.share({
                        title: msg.metadata.data.name,
                        text: `Check out ${msg.metadata.data.name} at ${msg.metadata.data.address}`,
                        url: msg.metadata.data.website || window.location.href
                      });
                    }
                  }}
                  className="mt-3 px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                >
                  Share
                </button>
              )}
              
              {/* Rating button */}
              {enableRatings && (
                <button
                  data-testid="rate-restaurant-button"
                  onClick={() => {
                    if (msg.metadata?.data) {
                      setRatingDialog({
                        restaurantId: msg.metadata.data.name,
                        restaurantName: msg.metadata.data.name
                      });
                      setSelectedRating(0);
                      setRatingComment('');
                    }
                  }}
                  className="mt-3 ml-2 px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600 transition-colors"
                >
                  Rate Restaurant
                </button>
              )}
            </div>
          )}
        </div>
        );
      })}
      
      {/* Rating Dialog */}
      {ratingDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Rate {ratingDialog.restaurantName}</h3>
            
            {/* Star Rating */}
            <div data-testid="rating-stars" className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  data-testid={`star-${star}`}
                  onClick={() => setSelectedRating(star)}
                  className={`text-2xl ${
                    star <= selectedRating ? 'text-yellow-400' : 'text-gray-300'
                  } hover:text-yellow-400 transition-colors`}
                >
                  ⭐
                </button>
              ))}
            </div>
            
            {/* Comment Input */}
            <textarea
              placeholder="Add a comment (optional)"
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded mb-4 resize-none"
              rows={3}
            />
            
            {/* Action Buttons */}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setRatingDialog(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedRating > 0) {
                    onSubmitRating?.({
                      restaurantId: ratingDialog.restaurantId,
                      rating: selectedRating,
                      comment: ratingComment
                    });
                    setRatingDialog(null);
                  }
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:bg-gray-300"
                disabled={selectedRating === 0}
              >
                Submit Rating
              </button>
            </div>
          </div>
        </div>
      )}
      
      {loading && (
        <div 
          data-testid="loading-indicator"
          className="px-3 py-2 self-start text-gray-500"
        >
          ...
        </div>
      )}
      
      {error && (
        <div 
          data-testid="error-message"
          className="px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200 self-start"
          role="alert"
        >
          {error.message}
        </div>
      )}
      
      </div>
      
    </div>
  );
};

export default ResponseDisplay;
export type { ResponseDisplayProps, Message, ChatError }; 